/**
 * Content for the standalone "How It Works" operations landing page (/how-it-works).
 * Translation: Content for the standalone "How It Works" operations landing page (/how-it-works).
 */
export const howItWorksSections = {
  hero: {
    badge: "For Healthcare Operations & IT Leaders",
    titleMain: "We Catch What Your Team ",
    titleAccent: "Misses.",
    description:
      "We run an always-on technology operations team behind your billing systems, patient-facing tools, and infrastructure — catching errors, outages, and security gaps before your staff or your patients do.",
    primaryCta: { label: "Get a Free System Health Check", href: "/#contact" },
  },

  problems: {
    eyebrow: "The Hidden Costs",
    title: "What's Slipping Through the Cracks Right Now",
    description:
      "This is the reality across healthcare operations today, industry-wide — not a description of what happens once we're already running your systems.",
    cards: [
      {
        title: "Numbers You Can't Fully Trust",
        description: "A report shows $0 or an obviously wrong total.",
        detail: "Staff spend hours manually cross-checking systems just to find where the number went missing.",
        icon: "numbers",
      },
      {
        title: "Systems That Break and Stay Broken",
        description: "An update fails quietly — and stays broken.",
        detail: "A patient portal or scheduling tool can sit down for a day or more before anyone notices, because nobody was watching it continuously.",
        icon: "systemBreak",
      },
      {
        title: "Security Gaps You Can't See",
        description: "Patient data lives across code, sites, and vendors.",
        detail: "A weak spot in any one of them can sit undiscovered for months.",
        icon: "securityGap",
      },
      {
        title: "A Patchwork of Vendors",
        description: "Different tools, different contracts, different monitoring — or none.",
        detail: "No single place to see what's healthy or what you're paying for.",
        icon: "patchwork",
      },
    ],
  },

  solution: {
    eyebrowLabel: "Control Plane · Corporate Brain Architecture",
    techNote: "controlPlane",
    title: "Full Visibility. Zero Unilateral Changes.",
    description:
      "An operations team that never clocks out. It watches your systems, investigates before touching anything, and only acts under strict safety rules.",
    detail:
      "It watches your financial reports, live systems, and security posture continuously. When something looks wrong, it investigates the way a careful analyst would — checking the real facts first, never guessing — and either fixes it under strict safety rules, or hands your team a clear, ready-to-approve fix. Every organization gets its own private, walled-off space — nothing is ever shared or visible across clients.",
    tiles: [
      { num: "01", title: "Financial Accuracy", description: "Catches and fixes reporting errors before they reach leadership." },
      { num: "02", title: "System Uptime", description: "Catches deployment problems before they cause an outage." },
      { num: "03", title: "Security & Compliance", description: "Continuous checks; never touches a live system without written authorization." },
      { num: "04", title: "Build & Launch", description: "Designs and launches new patient-facing systems on the same infrastructure that keeps them healthy." },
    ],
  },

  approach: {
    eyebrowLabel: "Orchestrator → Map → Reduce → Critic · Bounded Recursive Fan-Out",
    techNote: "orchestrator",
    title: "No Shortcuts Between Spotting a Problem and Fixing It.",
    description: "Every run follows the same four steps, in order, every time — watch, investigate, fix, verify.",
    detail: "No step gets skipped, and no agent marks its own work done.",
    footer:
      "“Investigation is always separated from fixing. The parts of the system that look for problems are never the same parts allowed to change anything.”",
    steps: [
      {
        number: "01",
        title: "Watch",
        description: "Continuous, automatic checks across your reports, systems, and code.",
      },
      {
        number: "02",
        title: "Investigate, Never Guess",
        description: "Real facts are checked directly at the source.",
        detail: "Two independent checks must agree on what's wrong before any action is taken.",
      },
      {
        number: "03",
        title: "Fix Under Strict Rules",
        description: "A backup is taken first.",
        detail: "Any change is small, targeted, and tested before it goes live.",
      },
      {
        number: "04",
        title: "Verify, Then Report",
        description: "An independent check confirms the fix worked.",
        detail: "Before it's marked resolved. You get a plain-language report of what happened.",
      },
    ],
  },

  engine: {
    eyebrowLabel: "Agentic MapReduce Swarm",
    techNote: "engine",
    title: "The Engine Behind Kanzi",
    description:
      "Every capability on this page runs on the same architecture — narrow agents investigate in parallel, one orchestrator converges their findings.",
    detail:
      "Your data feeds a swarm of narrow, read-only agents working in parallel — never one model guessing alone — and a single orchestrator reduces their findings into one verified answer before anything reaches a dashboard or touches a live system.",
    sourceNodes: [
      { lines: ["Financial", "Systems"] },
      { lines: ["Live", "Infrastructure"], mobileLines: ["Live", "Infra"] },
      { lines: ["Documents &", "Policies"], mobileLines: ["Docs &", "Policies"] },
    ],
    outputNodes: [
      { lines: ["Verified", "Fix"] },
      { lines: ["Audit", "Report"] },
      { lines: ["Live", "Dashboard"] },
    ],
    mainNode: { label: "MAIN AGENT", caption: "ORCHESTRATOR" },
    gridLabel: { label: "AGENT SWARM", caption: "81 PARALLEL, READ-ONLY AGENTS" },
    legend: [
      {
        title: "Map",
        description: "81 narrow, read-only agents investigate in parallel — each one scoped to a single question.",
      },
      {
        title: "Reduce",
        description: "One orchestrator converges every finding into a single, coordinated answer.",
      },
      {
        title: "Critic Gate",
        description: "Nothing reaches your dashboard, or a live system, without an independent pass first.",
      },
    ],
  },

  capabilities: {
    eyebrow: "The Capabilities",
    title: "Problem. Solution. Result.",
    items: [
      {
        label: "Financial Accuracy - Data-Reconciliation Swarm (MapReduce)",
        techNote: "cap01",
        title: "When Your Billing Dashboard Shows the Wrong Number",
        problem: "Your billing dashboard shows $0 in collections for the month.",
        solution: "An automated investigation checks the real numbers at the source first.",
        detail:
          "Tracking down where the number got lost across multiple systems can normally take hours or days — instead, the investigation checks whether the reporting system or the underlying data is at fault, and requires both checks to agree before anything is touched.",
        stat: { big: "$0 → $4.27M", caption: "Confirmed correct in ~75 minutes, zero side effects" },
      },
      {
        label: "System Uptime - Fable-Orchestrated CI/CD Stability Swarm",
        techNote: "cap02",
        title: "When an Update Breaks Something — and Nobody Notices for a Day",
        problem: "A deployment fails midway and stays broken.",
        solution: "Continuous monitoring catches risky changes before they go live.",
        detail:
          "Left alone, it can sit broken for a day or more, requiring manual digging across several tools to diagnose — instead, any failure is matched against a library of \"we've seen this before\" for an instant diagnosis and fix.",
        stat: { big: "~1 Day → Caught Early", caption: "Plus 5 finished features found sitting unreleased" },
      },
      {
        label: "Security & Compliance - SEC-SWARM-001 — Agentic MapReduce Security Swarm (\"SENTINEL\")",
        techNote: "cap03",
        title: "Finding the Weak Spot Before Someone Else Does",
        problem: "A single overlooked weakness can go unnoticed for months.",
        solution: "Continuous scanning ranks real risk instead of a list of \"maybes.\"",
        detail:
          "A weakness in code, websites, or a vendor connection can sit unnoticed until it becomes an entry point for a breach — any real-world test on a live system still requires your written authorization first, no exceptions.",
        stat: { big: "Ranked & Evidenced", caption: "Every finding proposed for approval, never auto-applied" },
      },
      {
        label: "Build & Launch - AgentOS — Automated Multi-Tenant Deploy Pipeline",
        techNote: "cap04",
        title: "We Don't Just Watch Your Systems. We Can Build Them Too.",
        problem: "New systems usually mean a handoff gap.",
        solution: "The same team that keeps your systems healthy can build new ones.",
        detail:
          "Normally a separate vendor builds it and no one who understands it keeps it running — here, the same team builds it and watches it from the moment it goes live.",
        stat: { big: "Zero Gap", caption: "Same team ships it and operates it, day one" },
      },
    ],
  },

  trust: {
    eyebrowLabel: "FSML Self-Learning Loop · ROUTE-001 Scope Registry",
    techNote: "trust",
    title: "The Guardrails Matter as Much as the Capability",
    items: [
      {
        title: "Nothing touches a live system without a human approving it first.",
        description: "The parts that investigate can never accidentally change anything.",
        icon: "Lock",
      },
      {
        title: "Real-world security tests always require your written sign-off in advance.",
        description: "No exceptions, no silent testing.",
        icon: "PenLine",
      },
      {
        title: "Every finding has to show its work.",
        description: "A fix is never applied, and an issue never marked resolved, on the word of just one check.",
        icon: "Search",
      },
      {
        title: "Your data never mixes with anyone else's.",
        description: "Every client gets a fully separate, private space.",
        icon: "Boxes",
      },
      {
        title: "Gets smarter over time, not noisier.",
        description: "The system learns which alerts were real versus false alarms, interrupting your team less the longer it runs.",
        icon: "TrendingUp",
      },
      {
        title: "Predictable, itemized costs.",
        description: "Every action is tracked, so billing is exact — no flat-rate guessing or surprise invoices.",
        icon: "CircleDollarSign",
      },
      {
        title: "Proven on our own business first.",
        description: "The same system runs this company's own internal operations, including its own live financial systems.",
        icon: "Home",
      },
      {
        title: "Every action leaves a full audit trail.",
        description: "Complete, timestamped visibility into what ran, when, and why — ready for compliance review at any time.",
        icon: "ClipboardList",
      },
    ],
  },

  comparison: {
    eyebrowLabel: "Kanzi vs. Generic AI Agents",
    techNote: "comparison",
    title: "Why Not Just Point a Generic AI Agent at This?",
    description:
      "We're often compared to general-purpose AI agent tools. Those tools are built to remember one person, or one project, in a single session — not to run a healthcare organization's operations with an audit trail a compliance reviewer would accept.",
    rows: [
      {
        capability: "Learns From Mistakes",
        us: {
          short: "Automatic, ongoing",
          detail: "Re-checks the exact question that exposed the gap before marking it solved.",
        },
        them: {
          status: "no",
          short: "Successes only",
          detail: "Only reinforces answers that already worked; never investigates the ones that didn't.",
        },
      },
      {
        capability: "Self-Grading Memory",
        us: {
          short: "Automatic",
          detail: "Promotes accurate facts, retires wrong ones, and flags contradictions automatically.",
        },
        them: {
          status: "partial",
          short: "Partial at best",
          detail: "At most a soft self-reminder to save notes — no outcome scoring, no contradiction checks.",
        },
      },
      {
        capability: "Self-Improving Config",
        us: {
          short: "Automatic, human-approved",
          detail: "Proposes its own upgrades on an ongoing basis; a human always signs off.",
        },
        them: { status: "no", short: "None", detail: "None do this." },
      },
      {
        capability: "Cross-System Mapping",
        us: {
          short: "Portfolio-wide",
          detail: "Self-updating map spanning every system and department.",
        },
        them: {
          status: "partial",
          short: "Session-scoped",
          detail: "At most one project mapped per session — no organization-wide view.",
        },
      },
      {
        capability: "Search Quality",
        us: {
          short: "Understands meaning",
          detail: "Not just keywords — automatically prioritizes recent information.",
        },
        them: {
          status: "no",
          short: "Keyword-only",
          detail: "Keyword-only search, or compressed session notes — none understand meaning across a full knowledge base.",
        },
      },
      {
        capability: "Memory Scale",
        us: {
          short: "450+ KB, full database",
          detail: "Thousands of reviewed facts, kept current automatically.",
        },
        them: {
          status: "no",
          short: "~2,200 characters",
          detail: "Typically capped at a couple thousand characters about one person, or notes scoped to a single project.",
        },
      },
      {
        capability: "Compute Economics",
        us: {
          short: "Scales on demand",
          detail: "Spins up only when indexing; permanent off-site backup.",
        },
        them: {
          status: "no",
          short: "Runs constantly",
          detail: "Runs constantly on one machine — no ability to scale up only when needed.",
        },
      },
      {
        capability: "Multi-Agent Review",
        us: {
          short: "15+ specialists",
          detail: "Standing specialists with review gates before work counts as done.",
        },
        them: {
          status: "partial",
          short: "Basic at best",
          detail: "Basic multi-agent support at best; helpers scoped to a single project.",
        },
      },
      {
        capability: "Data Isolation",
        us: {
          short: "Isolated per client",
          detail: "Separate environment per client and department, with routing enforced centrally.",
        },
        them: {
          status: "no",
          short: "Not built for it",
          detail: "Not built for more than one user or one project at a time.",
        },
      },
      {
        capability: "Auditable Transactions",
        us: {
          short: "Proven in production",
          detail: "Already proven running real transactions with real money.",
        },
        them: { status: "na", short: "Not designed for this", detail: "Not designed for this use case." },
      },
      {
        capability: "Self-Healing",
        us: {
          short: "Automatic repair",
          detail: "Detects and repairs itself automatically, with hourly health checks and safe restarts.",
        },
        them: {
          status: "no",
          short: "Mostly none",
          detail: "Mostly none — a person has to notice and fix it.",
        },
      },
      {
        capability: "Cites Sources",
        us: {
          short: "Always",
          detail: "Every factual claim traces back to a source document; nothing is silently deleted, only superseded.",
        },
        them: { status: "no", short: "Never", detail: "None cite sources for their claims." },
      },
      {
        capability: "Human Sign-Off",
        us: {
          short: "Required, enforced",
          detail: "Approvals routed to a human, hardened permission profiles, kill switches.",
        },
        them: {
          status: "partial",
          short: "Partial",
          detail: "Partial approval gates at best; not universal.",
        },
      },
      {
        capability: "Disaster Recovery",
        us: {
          short: "Zero data loss",
          detail: "Hardware migration with zero data loss, plus tested backup and recovery drills.",
        },
        them: {
          status: "no",
          short: "Undocumented",
          detail: "No documented recovery track record.",
        },
      },
    ],
    disclaimer:
      "“Generic AI agents” above refers to the three tools we're most often compared against: OpenClaw (open-source agent framework), Hermes Agent (Nous Research's personal memory-first agent, hard-capped at ~2,200 characters), and omp (a single-repository coding assistant). Sources: hermes-agent.org, glukhov.org, saulius.io (Hermes); betterstack.com, explainx.ai (omp); quefly.com, github.com/queflyhq (Open Memory Protocol). Comparison verified 2026-07-04.",
  },

  audienceFit: {
    eyebrowLabel: "Why Healthcare Organizations Pick Us",
    techNote: "audienceFit",
    title: "It Fits Differently Depending on Your Size",
    description:
      "The specific reasons vary by how you're organized — here's what tends to matter most at each end of the spectrum.",
    groups: [
      {
        id: "enterprise",
        label: "Hospital Systems & Health Networks",
        items: [
          {
            title: "Audit-ready memory, not guesswork.",
            description:
              "Every fact traces back to its source, and every fixed gap is re-verified against the question that exposed it — the audit trail a compliance reviewer would want to see.",
            icon: "ClipboardCheck",
          },
          {
            title: "Real data isolation, not just access rules.",
            description:
              "Each organization runs in its own isolated cloud environment, not a shared database with permissions layered on top — the security boundary regulators and underwriters ask about.",
            icon: "Boxes",
          },
          {
            title: "Nothing risky is left to the AI's judgment.",
            description:
              "Money and regulated transactions run through fixed, auditable logic. The AI researches and flags issues; it doesn't get the final say on the transaction itself.",
            icon: "ShieldCheck",
          },
          {
            title: "It keeps working when no one's watching it.",
            description:
              "Automatic health checks, self-healing recovery, nightly backups — built to run unattended, the same way your EHR or billing system has to.",
            icon: "Activity",
          },
          {
            title: "You're not locked into one AI vendor.",
            description:
              "If a model provider has an outage, the system falls back to the next one automatically — your operations don't stop because one AI company had a bad day.",
            icon: "Unlock",
          },
        ],
      },
      {
        id: "smallPractice",
        label: "Independent Practices & Clinics",
        items: [
          {
            title: "It runs itself overnight.",
            description:
              "Reviewing new documents, updating its knowledge, researching what it couldn't answer — none of it needs a staff member to babysit it.",
            icon: "Moon",
          },
          {
            title: "It gets smarter from its own mistakes — about your practice.",
            description:
              "Unanswered questions get researched and verified overnight, building real memory of your staff, vendors, and procedures. A personal AI remembers one person; ours remembers your organization.",
            icon: "Brain",
          },
          {
            title: "One product, not a consulting project.",
            description:
              "A chat interface, your own knowledge library, and a button to reach a human engineer 24/7. No months-long integration, no risky action without a human approving it first.",
            icon: "Package",
          },
          {
            title: "One predictable bill.",
            description:
              "Start on shared infrastructure; upgrade to a faster, dedicated brain later if you need it. It's an add-on, not a re-platform.",
            icon: "Receipt",
          },
          {
            title: "It's a full team, not one generalist assistant.",
            description:
              "15+ standing specialists work together, with review gates before anything counts as done — instead of a single assistant guessing its way through everything on its own.",
            icon: "Users",
          },
        ],
      },
    ],
  },

  platformNotes: {
    eyebrowLabel: "Beyond The Core Capabilities",
    techNote: "platformGeneral",
    title: "Additional Platform Capabilities",
    description:
      "Two more pieces of the same architecture, for anyone evaluating the platform itself rather than a single capability.",
    cards: [
      {
        label: "Research & Reporting - STORM Research Lane",
        techNote: "storm",
        title: "We Can Research and Write for You, Too",
        description: "The same team can produce research and reports on demand.",
        detail:
          "Competitive analysis, compliance briefs, or onboarding content — with every claim backed by a source, never just an AI guess.",
        viz: "storm",
      },
      {
        label: "Extensibility - Swarm-Kit Template",
        techNote: "swarmkit",
        title: "Not Just Four Fixed Capabilities",
        description: "These aren't one-off tools — they're built from the same reusable blueprint.",
        detail:
          "If your organization runs into a problem that doesn't fit neatly into any of them, we can stand up a new, purpose-built specialist the same way, under the same human-approval rules.",
        viz: "swarmkit",
      },
    ],
  },

  faq: {
    eyebrow: "Frequently Asked",
    title: "Questions Administrators Ask Us First",
    items: [
      {
        question: "Does this replace our IT team?",
        answer: "No. It handles the repetitive, time-consuming detective work so your team can focus on decisions, not digging through logs.",
        defaultOpen: true,
      },
      {
        question: "Will this have access to our patient data / EHR?",
        answer: "Access is scoped only to what's needed for the specific capability you turn on, and every organization's data is kept in a completely separate, private space.",
      },
      {
        question: "What happens if it makes a mistake?",
        answer: "Nothing is applied to a live system without passing safety checks first, and nothing is marked \"resolved\" without an independent second check. If something is ambiguous, the system stops and asks — it does not guess.",
      },
      {
        question: "How is this different from a regular monitoring or alert tool?",
        answer: "Most tools tell you that something is wrong. This investigates why, checks the real facts at the source, and can propose or apply a verified fix instead of generating another alert to chase down manually.",
      },
      {
        question: "How long does it take to get started?",
        answer: "Financial accuracy and system-uptime monitoring can typically be pointed at your existing systems without a rebuild. Security scanning and new system builds are scoped and authorized with your team first.",
      },
    ],
  },

  finalCta: {
    title: "See What's Actually Happening in Your Systems Right Now",
    description:
      "Start with a free, no-obligation health check — we'll show you, in plain language, what we'd catch if we were already watching your systems.",
    primaryCta: { label: "Get a Free System Health Check", href: "/#contact" },
  },

  // Technical-detail copy surfaced via the small "i" info buttons next to eyebrows.
  techNotes: {
    controlPlane:
      "One shared corporate brain — a unified memory layer (document index, semantic recall, code-graph, per-agent workspace memory, knowledge base) — sits behind every deployment. Every unit of work carries a scope_key (tenant:client:project:dept:task) enforced by a central scope registry, so agents share one control plane but can never read or write outside their assigned tenant.",
    orchestrator:
      "Built on an Orchestrator → Executor → Critic loop extended with two governed patterns: a STORM research lane (perspective-capped, citation-gated retrieval) and bounded recursive fan-out for open-ended goals. Every expansion requires approval from a Budget/Depth Gate (max_depth, max_fanout, token budget, wall-clock cap) — no agent self-authorizes its own recursion or completion. First-class HALT conditions (missing_credentials, destructive_ambiguity, conflicting_requirements, missing_authorization) force a stop instead of a guess, backstopped by heartbeat \"subagent orphan detection.\"",
    engine:
      "The grid visualizes the MAP phase of the platform's core pattern: independent, read-only agents — each one scoped to a single narrow question (one repo, one endpoint, one query-path) — run in parallel and are never permitted to write. Their findings are reduced by a single Orchestrator, the only role allowed to establish ground truth and sequence a fix, mirroring the same Orchestrator → Map → Reduce → Critic discipline referenced in \"Approach\" above and the 4-role MapReduce pattern used in the Financial-Accuracy swarm (cap01) and the MAP/SHUFFLE/REDUCE security swarm (cap03). Fan-out width is capped and gated the same way — no agent self-authorizes its own recursion or completion.",
    cap01:
      "A 4-role MapReduce pattern: the Orchestrator establishes ground truth once (Phase 0, never delegated), Shard A (query-path investigator) and Shard B (data-layer investigator) run read-only in parallel (Phase 1: Map) and must converge on one \"coordinate signal\" before an Executor applies anchored, gated patches (Phase 3). Reference incident: Kanzi May-2026 \"$0-collections\" — Snowflake $3.86M vs. thor.db $4.28M vs. dashboard $0, resolved via 6 anchored patches with exact rollup tie-out.",
    cap02:
      "Three specialist agents run under one orchestrator (Fable), which only aggregates and sequences — it never edits infrastructure or code directly: CFN Drift Sentinel (scheduled detect-stack-drift sweeps, benign-vs-blocking classification), Deploy Triage (CodePipeline → CodeBuild → CloudWatch Logs → CloudFormation root-cause chaining against a failure-signature library), and PR Merge Guard (tracks dev↔master divergence, drafts merge PRs). Reference incident: thormedai AWS account, 2026-07-05 — thor-secrets-api-dev / thor-datalake-api-dev stack failures.",
    cap03:
      "MAP fans out narrow, read-only workers (one repo/endpoint/lockfile each) for SAST/DAST/SCA/secret-scanning; SHUFFLE groups findings by CWE/asset via a dedupe_hash; REDUCE correlates them into exploit chains and scores business risk; a mandatory Critic gate rejects any uncited high-severity finding. A STORM research role pulls CVE/advisory evidence under a hard citation gate. Every run is scoped by a tenant:client:project:dept:security:task key, and any active/DAST probe against a live endpoint is denied by the fan-out gate absent recorded owner authorization — passive-only is the automatic default. Proposed caps: depth 3 / fanout 6 / token budget 400k / wall-clock 60 min.",
    cap04:
      "New client systems are scaffolded by three reusable \"system-build\" skills — AWS Cloud-Native Deploy (Serverless Framework, CloudFormation, CodePipeline/CodeBuild, Cognito, Lambda, API Gateway), React PWA Dev Patterns, and Native App Patterns — wired to a CI/CD spine (git push → CodePipeline → CodeBuild → CloudFormation) that deploys into an isolated AWS sub-account under our AWS Organization, vended per client with its own control plane and swarm workers, metered per scope_key for exact billing.",
    trust:
      "Self-learning runs on FSML (recall → outcome → score), scoped per client/department, so detectors and fixes are ranked by real-world precision over time. Tenant isolation is enforced by a central scope registry (ROUTE-001): every task carries a scope_key, and no agent can read or write outside its assigned scope — one shared corporate brain, strictly partitioned views. A heartbeat watchdog with \"subagent orphan detection\" is the automatic backstop kill-switch across every swarm.",
    comparison:
      "Verified-recall rate is measured by FSML's outcome resolver, which re-tests the original failed query before a gap is marked closed — not a self-report. Memory-scale figures (450+ KB of source documents, thousands of scored candidate facts, full Postgres-backed retrieval) are read live from the production control plane. Tenant isolation is enforced by the same ROUTE-001 scope registry referenced above. Full row-by-row sourcing for the comparison itself is in the disclaimer directly below the table.",
    audienceFit:
      "Isolation is enforced by the same ROUTE-001 scope registry (tenant:client:project:dept:task) referenced elsewhere on this page — an AWS sub-account per client, not a shared database with row-level permissions. Model-provider fallback runs a ladder (1M-context frontier → Opus → Sonnet → OSS) so a single provider outage degrades service rather than stopping it. The zero-data-loss hardware migration referenced here completed 2026-07-04 and is the same event cited in the comparison table above.",
    platformGeneral:
      "These two blocks aren't separate products — they're two structural properties of the same platform. Every deployment includes access to a governed research/reporting engine that can be pointed at a new topic on demand, and the platform itself is assembled from one reusable \"swarm\" template (conductor + workers + critic + safety caps), so a new, problem-specific capability can be built the same way instead of a custom one-off — always under the same human-approval rules as everything else on this page.",
    storm:
      "A parallel research pattern (STORM — Synthesis of Topic Outlines through Retrieval and Multi-perspective question-asking): perspective-capped (≤5), max-rounds-capped Q&A loops with a hard citation gate — a Critic rejects uncited claims. Used both standalone (competitive research, compliance briefs, client onboarding content) and embedded inside SENTINEL to source CVE/advisory evidence.",
    swarmkit:
      "Every capability above is one instance of the same reusable template: {conductor agent, worker role(s), critic, budget/depth caps, shared memory + Mission Control hooks}. A /swarm new workflow scaffolds new, problem-specific swarms the same way — always gated by human/architect approval, never spun up autonomously.",
  },
};
